import type { CardDto } from './types';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Whole dollars with thousands separators, e.g. "$120,000". */
export function formatMoney(amount: number): string {
  return currency.format(amount);
}

/**
 * "$40,000 one-time · $120,000/yr" when both are set, one part when only one is set, and null
 * when neither is.
 */
export function formatRoi(card: Pick<CardDto, 'roiOneTime' | 'roiAnnual'>): string | null {
  const parts: string[] = [];
  if (card.roiOneTime !== null && card.roiOneTime !== undefined) parts.push(`${currency.format(card.roiOneTime)} one-time`);
  if (card.roiAnnual !== null && card.roiAnnual !== undefined) parts.push(`${currency.format(card.roiAnnual)}/yr`);
  return parts.length ? parts.join(' · ') : null;
}

/** Build cost in whole dollars, e.g. "$12,000". */
export function formatBuildCost(cost: number): string {
  return currency.format(cost);
}

const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Splits "yyyy-MM-dd" into local calendar parts (no Date/UTC parsing, so the day never shifts). */
function dateParts(iso: string): { year: number; dayMonth: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(m[1]), dayMonth: `${Number(m[3])} ${shortMonths[month - 1]}` };
}

/** "yyyy-MM-dd" as "3 Dec" in the current year, else "3 Dec 2027". Unparseable input is returned as is. */
export function formatShortDate(iso: string, today: Date = new Date()): string {
  const p = dateParts(iso);
  if (!p) return iso;
  return p.year === today.getFullYear() ? p.dayMonth : `${p.dayMonth} ${p.year}`;
}

/** An ISO timestamp in local time as "20 Oct 2026, 14:32". Unparseable input is returned as is. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()} ${shortMonths[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "yyyy-MM-dd" as "3 Dec 2026", always with the year. Unparseable input is returned as is. */
export function formatFullDate(iso: string): string {
  const p = dateParts(iso);
  return p ? `${p.dayMonth} ${p.year}` : iso;
}
